<template>
  <section class="mobile-notifications-page">
    <header class="mb-5">
      <div class="overline primary--text font-weight-bold">INBOX</div>
      <h1 class="text-h4 font-weight-bold mb-1">Notifications</h1>
      <p class="admin-muted mb-0">Your store alerts, saved to your {{ brandName }} account.</p>
    </header>

    <v-alert v-if="status !== 'enabled'" type="info" outlined class="rounded-xl mb-5">
      <div class="font-weight-medium">{{ permissionTitle }}</div>
      <div class="text-body-2">{{ permissionMessage }}</div>
      <v-btn v-if="canEnable" color="primary" class="mt-3" :loading="enabling" @click="enable">{{ status === 'error' ? 'Try again' : 'Enable notifications' }}</v-btn>
    </v-alert>

    <div class="notification-search-row d-flex align-center mb-5">
      <v-text-field v-model.trim="query" outlined dense rounded clearable hide-details prepend-inner-icon="mdi-magnify" label="Search notifications" class="notification-search-filter" />
      <v-btn icon class="ml-2 notification-touch-target" :loading="loading && !loadingOlder" aria-label="Refresh notifications" @click="refresh"><v-icon>mdi-refresh</v-icon></v-btn>
      <v-badge :value="activeFilterCount" :content="activeFilterCount" color="primary" overlap>
        <v-btn icon class="ml-2 notification-touch-target" :aria-label="filterButtonLabel" @click="openFilters"><v-icon>mdi-filter-variant</v-icon></v-btn>
      </v-badge>
    </div>

    <div v-if="unreadCount" class="text-caption font-weight-medium primary--text mb-4" role="status">{{ unreadCount }} unread</div>

    <v-alert v-if="loadError" type="warning" outlined dense class="rounded-xl mb-5">
      <div>{{ history.length ? "Showing alerts saved on this phone. Connect to the internet to refresh your account history." : "Notification history could not be loaded, and no saved alerts match these filters." }}</div>
      <v-btn text small color="primary" class="mt-2 notification-touch-target" :loading="loading" @click="refresh">Try again</v-btn>
    </v-alert>

    <v-card v-if="!filtered.length && !loading && !loadError" outlined class="rounded-xl pa-7 text-center elevation-0">
      <v-icon size="52" color="primary" class="mb-3">mdi-bell-outline</v-icon>
      <h2 class="text-h6 font-weight-bold mb-2">{{ emptyTitle }}</h2>
      <p class="admin-muted mb-0">{{ emptyMessage }}</p>
    </v-card>

    <div v-if="loading && !history.length" class="mb-5" role="status" aria-live="polite">
      <v-progress-linear indeterminate color="primary" class="mb-2" />
      <div class="text-caption admin-muted text-center">Loading notification history…</div>
    </div>

    <div v-for="group in grouped" :key="group.label" class="mb-5">
      <h2 class="text-subtitle-2 font-weight-bold admin-muted mb-2">{{ group.label }}</h2>
      <v-card outlined class="rounded-xl elevation-0 overflow-hidden">
        <v-list two-line class="py-0">
          <template v-for="(item, index) in group.items">
            <v-divider v-if="index" :key="`${item.id}-divider`" inset />
            <v-list-item
              :key="item.id"
              :class="['notification-item--linked', { 'notification-item--unread': !item.readAt }]"
              :aria-label="`${item.readAt ? '' : 'Unread notification. '}${alertPresentation(item).label}. ${item.title}`"
              @click="showDetails(item)"
            >
              <v-list-item-avatar
                :color="alertPresentation(item).color"
                aria-hidden="true"
              >
                <v-icon :dark="alertPresentation(item).dark" :color="alertPresentation(item).iconColor">
                  {{ alertPresentation(item).icon }}
                </v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  <v-icon v-if="!item.readAt" x-small color="primary" aria-hidden="true" class="mr-1">mdi-circle</v-icon>
                  <span v-for="(part, partIndex) in highlightParts(item.title)" :key="`${item.id}-title-${partIndex}`" :class="part.match ? [primaryTextClass, 'notification-match'] : []">{{ part.text }}</span>
                </v-list-item-title>
                <v-list-item-subtitle v-if="item.body"><span v-for="(part, partIndex) in highlightParts(item.body)" :key="`${item.id}-body-${partIndex}`" :class="part.match ? [primaryTextClass, 'notification-match'] : []">{{ part.text }}</span></v-list-item-subtitle>
                <div v-if="context(item)" class="text-caption font-weight-medium primary--text mt-1"><span v-for="(part, partIndex) in highlightParts(context(item))" :key="`${item.id}-context-${partIndex}`" :class="part.match ? [primaryTextClass, 'notification-match'] : []">{{ part.text }}</span></div>
                <div class="text-caption admin-muted mt-1">{{ time(item.receivedAt) }}</div>
              </v-list-item-content>
              <v-list-item-icon v-if="notificationAction(item).path"><v-icon>mdi-chevron-right</v-icon></v-list-item-icon>
            </v-list-item>
          </template>
        </v-list>
      </v-card>
    </div>

    <div v-if="hasMore && !loadError" class="text-center mb-5">
      <v-btn rounded outlined color="primary" class="notification-touch-target" :loading="loadingOlder" :disabled="loading && !loadingOlder" @click="loadMore">Load older notifications</v-btn>
    </div>

    <v-dialog v-model="filterDialog" max-width="560" content-class="draggable-dialog notification-filter-dialog" persistent v-drag-dialog aria-labelledby="notification-filter-title">
      <v-card class="notification-filter-card rounded-xl">
        <CardToolbarDialog title="Filter notifications" title-id="notification-filter-title" icon="mdi-filter-variant" @cancel="cancelFilters" />
        <v-card-text class="notification-filter-body">
          <fieldset class="notification-filter-fieldset mb-6">
            <legend class="text-subtitle-1 font-weight-bold mb-2">Date range</legend>
            <v-radio-group v-model="draftTimeRange" hide-details class="mt-0">
              <v-radio v-for="option in timeRangeOptions" :key="option.value" :label="option.label" :value="option.value" class="notification-filter-option" />
            </v-radio-group>
          </fieldset>

          <fieldset class="notification-filter-fieldset">
            <legend class="text-subtitle-1 font-weight-bold mb-1">Notification type</legend>
            <p class="text-body-2 admin-muted mb-2">Choose one or more types, or leave All types selected.</p>
            <v-checkbox :input-value="draftTypes.length === 0" label="All types" hide-details class="notification-filter-option mt-0" @change="selectAllTypes" />
            <v-checkbox
              v-for="option in notificationTypeOptions"
              :key="option.value"
              v-model="draftTypes"
              :value="option.value"
              hide-details
              class="notification-filter-option mt-0"
            >
              <template #label>
                <v-avatar :color="option.color" size="32" class="mr-3" aria-hidden="true">
                  <v-icon small :dark="option.dark" :color="option.iconColor">{{ option.icon }}</v-icon>
                </v-avatar>
                <span>{{ option.label }}</span>
              </template>
            </v-checkbox>
          </fieldset>
        </v-card-text>
        <v-divider />
        <v-card-actions class="notification-filter-actions">
          <v-btn text class="notification-touch-target" @click="resetDraftFilters">Reset</v-btn>
          <v-spacer />
          <v-btn text class="notification-touch-target" @click="cancelFilters">Cancel</v-btn>
          <v-btn rounded color="primary" class="notification-touch-target" @click="applyFilters">Apply filters</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-bottom-sheet v-model="detailOpen" content-class="notification-detail-sheet">
      <v-card v-if="selectedNotification" class="notification-detail-sheet__card rounded-t-xl">
        <v-card-title class="notification-detail-sheet__header">
          <span>Notification details</span>
          <v-btn icon aria-label="Close notification details" @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="notification-detail-sheet__body">
          <div class="d-flex align-center mb-4">
            <v-avatar
              :color="alertPresentation(selectedNotification).color"
              aria-hidden="true"
              size="44"
              class="mr-3 flex-shrink-0"
            >
              <v-icon
                :dark="alertPresentation(selectedNotification).dark"
                :color="alertPresentation(selectedNotification).iconColor"
              >
                {{ alertPresentation(selectedNotification).icon }}
              </v-icon>
            </v-avatar>
            <span class="text-subtitle-2 font-weight-medium">{{ alertPresentation(selectedNotification).label }}</span>
          </div>
          <h2 class="text-h6 font-weight-bold mb-2">{{ selectedNotification.title }}</h2>
          <div v-if="context(selectedNotification)" class="text-subtitle-2 primary--text font-weight-medium mb-2">{{ context(selectedNotification) }}</div>
          <div class="text-caption admin-muted mb-4">{{ fullDate(selectedNotification.receivedAt) }}</div>
          <p v-if="selectedNotification.body" class="notification-detail-sheet__message mb-5">{{ selectedNotification.body }}</p>
        </v-card-text>
        <v-card-actions v-if="selectedAction.path" class="notification-detail-sheet__actions">
          <v-btn block rounded color="primary" @click="openAction">
            {{ selectedAction.label }}
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-bottom-sheet>
  </section>
</template>

<script>
import {
  cacheMobileNotificationHistory,
  enableMobileNotifications,
  getMobileNotificationHistory,
  getMobileNotificationStatus,
  getServerNotificationHistory,
  markServerNotificationRead,
  mobileNotificationAction,
} from "./service";
import { mobileBrand } from "./brand";

export default {
  name: "MobileNotifications",
  data: () => ({
    query: "", history: [], localHistory: [], detailOpen: false, selectedNotification: null,
    status: "prompt", enabling: false, loading: false, loadingOlder: false, loadError: false,
    total: 0, unreadCount: 0, limit: 20, serverOffset: 0, searchTimer: null, loadRequestId: 0,
    timeRange: "all", draftTimeRange: "all", selectedTypes: [], draftTypes: [], filterDialog: false,
    timeRangeOptions: [
      { label: "All time", value: "all" },
      { label: "Last 7 days", value: 7 },
      { label: "Last 30 days", value: 30 },
      { label: "Last 90 days", value: 90 },
    ],
    notificationTypeOptions: [
      { label: "Store opened", value: "store-opened", icon: "mdi-store-check-outline", color: "success darken-2", dark: true },
      { label: "Shift closed", value: "shift-closed", icon: "mdi-cash-multiple", color: "primary", dark: true },
      { label: "Invoice or estimate deleted", value: "invoice-deleted", icon: "mdi-file-document-remove-outline", color: "error", dark: true },
      { label: "Scheduled sales summary", value: "sales-summary", icon: "mdi-clock-outline", color: "info", dark: true },
      { label: "Green target reached", value: "target-green", icon: "mdi-target", color: "success", iconColor: "grey darken-4", dark: false },
      { label: "Yellow target reached", value: "target-yellow", icon: "mdi-target", color: "warning", iconColor: "grey darken-4", dark: false },
      { label: "Test notification", value: "test", icon: "mdi-flask-outline", color: "info", dark: true },
      { label: "Other notifications", value: "other", icon: "mdi-bell-outline", color: "primary", dark: true },
    ],
  }),
  computed: {
    brandName() { return mobileBrand.name; },
    primaryTextClass() { return this.$vuetify.theme.dark ? "primary--text text--lighten-4" : "primary--text"; },
    hasMore() { return this.serverOffset < this.total; },
    activeFilterCount() { return (this.timeRange === "all" ? 0 : 1) + (this.selectedTypes.length ? 1 : 0); },
    filterButtonLabel() { return this.activeFilterCount ? `Filter notifications, ${this.activeFilterCount} active` : "Filter notifications"; },
    emptyTitle() {
      if (this.query || this.selectedTypes.length) return "No matching notifications";
      if (this.timeRange !== "all") return "No notifications in this time range";
      return "No notifications yet";
    },
    emptyMessage() {
      if (this.query || this.selectedTypes.length) return "Try a different search or change your filters.";
      if (this.timeRange !== "all") return "Choose a longer time range to look further back.";
      return "Alerts you receive will appear here so you can find them later.";
    },
    selectedAction() { return mobileNotificationAction(this.selectedNotification || {}); },
    canEnable() { return this.status === "error" || ["prompt", "prompt-with-rationale"].includes(this.status); },
    permissionTitle() {
      if (this.status === "unavailable") return "Alerts are not available in this test build";
      if (this.status === "denied") return "Notifications are turned off";
      if (this.status === "error") return "Notifications could not be enabled";
      return "Stay up to date";
    },
    permissionMessage() {
      if (this.status === "unavailable") return "You can still browse your saved alerts.";
      if (this.status === "denied") return `Allow notifications for ${this.brandName} in your phone settings to receive alerts.`;
      if (this.status === "error") return "Check your connection and try again. Your saved alert history is still available.";
      return "Enable alerts to receive important store activity on this phone.";
    },
    filtered() {
      if (!this.selectedTypes.length) return this.history;
      return this.history.filter((item) => this.selectedTypes.includes(this.notificationType(item)));
    },
    grouped() {
      const groups = [];
      this.filtered.forEach((item) => {
        const date = new Date(item.receivedAt);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        let label = date.toDateString() === today.toDateString() ? "Today" : date.toDateString() === yesterday.toDateString() ? "Yesterday" : date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
        let group = groups.find((entry) => entry.label === label);
        if (!group) { group = { label, items: [] }; groups.push(group); }
        group.items.push(item);
      });
      return groups;
    },
  },
  watch: {
    query() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.refresh(), 300);
    },
  },
  mounted() {
    this.loadLocal();
    this.refresh();
    this.refreshStatus();
    window.addEventListener("repairnest-notification-history-changed", this.loadLocal);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    window.removeEventListener("repairnest-notification-history-changed", this.loadLocal);
  },
  methods: {
    async refreshStatus() {
      try { this.status = await getMobileNotificationStatus(); } catch (error) { this.status = "error"; }
    },
    async enable() {
      this.enabling = true;
      try { this.status = await enableMobileNotifications(); } catch (error) { this.status = "error"; }
      finally { this.enabling = false; }
    },
    loadLocal() {
      this.localHistory = getMobileNotificationHistory();
      if (this.loadError) this.useLocalFallback();
    },
    useLocalFallback() {
      const query = (this.query || "").toLowerCase();
      const days = Number(this.timeRange);
      const cutoff = Number.isFinite(days) ? Date.now() - (days * 24 * 60 * 60 * 1000) : null;
      this.history = this.localHistory.filter((item) => {
        const matchesSearch = !query || `${item.title} ${item.body} ${item.organization || ""} ${item.location || ""}`.toLowerCase().includes(query);
        const matchesTime = cutoff === null || new Date(item.receivedAt).getTime() >= cutoff;
        return matchesSearch && matchesTime;
      });
      this.total = this.history.length;
      this.serverOffset = this.history.length;
      this.unreadCount = this.history.filter((item) => !item.readAt).length;
    },
    async refresh() {
      await this.loadServer(false);
      await this.loadUntilTypeMatch();
    },
    async loadMore() {
      const visibleCount = this.filtered.length;
      await this.loadServer(true);
      if (this.selectedTypes.length && this.filtered.length === visibleCount) await this.loadUntilTypeMatch(visibleCount + 1);
    },
    async loadUntilTypeMatch(minimum = this.limit) {
      if (!this.selectedTypes.length || this.loadError) return;
      while (this.hasMore && this.filtered.length < minimum) {
        const previousOffset = this.serverOffset;
        await this.loadServer(true);
        if (this.loadError || this.serverOffset <= previousOffset) break;
      }
    },
    async loadServer(append) {
      const requestId = ++this.loadRequestId;
      const search = this.query || undefined;
      this.loading = true;
      this.loadingOlder = append;
      this.loadError = false;
      try {
        const result = await getServerNotificationHistory({
          limit: this.limit,
          offset: append ? this.serverOffset : 0,
          search,
          ...(this.timeRange === "all" ? {} : { days: this.timeRange }),
        });
        if (requestId !== this.loadRequestId) return;
        this.history = append
          ? this.history.concat(result.items).filter((item, index, all) => all.findIndex((candidate) => candidate.id === item.id) === index)
          : result.items;
        this.total = result.count;
        this.serverOffset = result.offset + result.items.length;
        this.unreadCount = result.unreadCount;
        this.localHistory = cacheMobileNotificationHistory(this.history.concat(this.localHistory));
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.loadError = true;
        this.useLocalFallback();
      } finally {
        if (requestId === this.loadRequestId) {
          this.loading = false;
          this.loadingOlder = false;
        }
      }
    },
    time(value) { return new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); },
    fullDate(value) { return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); },
    context(item) { return [item.organization, item.location].filter(Boolean).join(" · "); },
    openFilters() {
      this.draftTimeRange = this.timeRange;
      this.draftTypes = this.selectedTypes.slice();
      this.filterDialog = true;
    },
    cancelFilters() {
      this.filterDialog = false;
      this.draftTimeRange = this.timeRange;
      this.draftTypes = this.selectedTypes.slice();
    },
    resetDraftFilters() {
      this.draftTimeRange = "all";
      this.draftTypes = [];
    },
    selectAllTypes(selected) {
      if (selected) this.draftTypes = [];
    },
    async applyFilters() {
      const rangeChanged = this.timeRange !== this.draftTimeRange;
      const typesChanged = this.selectedTypes.join("|") !== this.draftTypes.join("|");
      this.timeRange = this.draftTimeRange;
      this.selectedTypes = this.draftTypes.slice();
      this.filterDialog = false;
      if (rangeChanged) await this.refresh();
      else if (typesChanged) await this.loadUntilTypeMatch();
    },
    notificationType(item) {
      const iconKey = String(item.iconKey || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const type = String(item.type || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const message = `${item.title || ""} ${item.body || ""}`.toLowerCase();
      const source = `${iconKey} ${type} ${message}`;
      if (/storeopened|storeopen|locationopened|shiftopened|store opened|store open/.test(source)) return "store-opened";
      if (/shiftclosed|shiftclose|shift closed|shift close/.test(source)) return "shift-closed";
      if (/invoicedeleted|invoicedelete|estimatedeleted|itemdeleted|invoice deleted|estimate deleted/.test(source)) return "invoice-deleted";
      if (/salesalert|nightlysale|salessummary|scheduledsales|timealert|dailysales|sales summary|sales update/.test(source)) return "sales-summary";
      if (/targetgreen|lightstatusgreen|green target|profit mode/.test(source) || type === "green") return "target-green";
      if (/targetyellow|lightstatusyellow|yellow target|break-even|break even/.test(source) || type === "yellow") return "target-yellow";
      if (type === "test" || iconKey === "test") return "test";
      return "other";
    },
    highlightParts(value) {
      const text = String(value || "");
      const query = String(this.query || "").trim().toLowerCase();
      if (!query) return [{ text, match: false }];
      const parts = [];
      let offset = 0;
      let matchIndex = text.toLowerCase().indexOf(query);
      while (matchIndex !== -1) {
        if (matchIndex > offset) parts.push({ text: text.slice(offset, matchIndex), match: false });
        parts.push({ text: text.slice(matchIndex, matchIndex + query.length), match: true });
        offset = matchIndex + query.length;
        matchIndex = text.toLowerCase().indexOf(query, offset);
      }
      if (offset < text.length) parts.push({ text: text.slice(offset), match: false });
      return parts;
    },
    alertPresentation(item = {}) {
      const iconKey = String(item.iconKey || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const type = String(item.type || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const message = `${item.title || ""} ${item.body || ""}`.toLowerCase();
      const matches = (...values) => values.some((value) => type.includes(value) || message.includes(value));
      const presentationByIconKey = {
        targetgreen: { icon: "mdi-target", color: "success", iconColor: "grey darken-4", label: "Green target reached", dark: false },
        targetyellow: { icon: "mdi-target", color: "warning", iconColor: "grey darken-4", label: "Yellow target reached", dark: false },
        invoicedeleted: { icon: "mdi-file-document-remove-outline", color: "error", label: "Invoice deleted", dark: true },
        shiftclosed: { icon: "mdi-cash-multiple", color: "primary", label: "Shift closed", dark: true },
        storeopened: { icon: "mdi-store-check-outline", color: "success darken-2", label: "Store opened", dark: true },
        salessummary: { icon: "mdi-clock-outline", color: "info", label: "Sales alert", dark: true },
        test: { icon: "mdi-flask-outline", color: "info", label: "Test notification", dark: true },
        general: { icon: "mdi-bell-outline", color: "primary", label: "General notification", dark: true },
      };

      if (presentationByIconKey[iconKey]) return presentationByIconKey[iconKey];

      if (type === "green" || matches("targetgreen", "lightstatusgreen", "green target", "profit mode")) {
        return { icon: "mdi-target", color: "success", iconColor: "grey darken-4", label: "Green target reached", dark: false };
      }
      if (type === "yellow" || matches("targetyellow", "lightstatusyellow", "yellow target", "break-even", "break even")) {
        return { icon: "mdi-target", color: "warning", iconColor: "grey darken-4", label: "Yellow target reached", dark: false };
      }
      if (matches("invoicedeleted", "invoicedelete", "itemdeleted", "invoice deleted", "invoice delete")) {
        return { icon: "mdi-file-document-remove-outline", color: "error", label: "Invoice deleted", dark: true };
      }
      if (matches("shiftclosed", "shiftclose", "shift closed", "shift close")) {
        return { icon: "mdi-cash-multiple", color: "primary", label: "Shift closed", dark: true };
      }
      if (matches("storeopened", "storeopen", "locationopened", "shiftopened", "store opened", "store open", "location opened")) {
        return { icon: "mdi-store-check-outline", color: "success darken-2", label: "Store opened", dark: true };
      }
      if (matches("salesalert", "nightlysale", "salessummary", "scheduledsales", "timealert", "dailysales", "sales summary", "sales update")) {
        return { icon: "mdi-clock-outline", color: "info", label: "Sales alert", dark: true };
      }
      if (type === "test") {
        return { icon: "mdi-flask-outline", color: "info", label: "Test notification", dark: true };
      }
      return { icon: "mdi-bell-outline", color: "primary", label: "General notification", dark: true };
    },
    showDetails(item) {
      this.selectedNotification = item;
      this.detailOpen = true;
      if (!item.readAt) {
        const readAt = new Date().toISOString();
        this.$set(item, "readAt", readAt);
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        cacheMobileNotificationHistory(this.history);
        markServerNotificationRead(item.id).catch(() => {});
      }
    },
    notificationAction(item) { return mobileNotificationAction(item); },
    openAction() {
      if (!this.selectedAction.path) return;
      this.detailOpen = false;
      if (this.selectedAction.path !== this.$route.fullPath) this.$router.push(this.selectedAction.path).catch(() => {});
    },
  },
};
</script>

<style scoped>
.mobile-notifications-page { margin: 0 auto; max-width: 760px; }
.notification-item--linked { cursor: pointer; }
.notification-item--unread { background: rgba(0, 0, 0, 0.035); }
.theme--dark .notification-item--unread { background: rgba(255, 255, 255, 0.06); }
.notification-touch-target { min-height: 44px; min-width: 44px; }
.notification-match { font-weight: 700; text-decoration: underline; text-underline-offset: 3px; }
.notification-search-row { min-width: 0; }
.notification-search-filter { flex: 1 1 auto; min-width: 0; }
.notification-filter-fieldset { border: 0; margin: 0; min-width: 0; padding: 0; }
.notification-filter-option { min-height: 44px; }
</style>

<style>
.notification-filter-card { display: flex; flex-direction: column; max-height: min(86vh, 720px); overflow: hidden; }
.notification-filter-body { min-height: 0; overflow-y: auto; padding: 20px 24px; }
.notification-filter-actions { flex: 0 0 auto; padding: 12px 16px; }
@media (max-width: 600px) {
  .notification-filter-body { padding: 16px; }
  .notification-filter-actions { flex-wrap: wrap; }
}
.notification-detail-sheet { margin: 0 auto !important; max-width: 640px; width: 100%; }
.notification-detail-sheet__card { display: flex; flex-direction: column; max-height: min(80vh, 720px); overflow: hidden; }
.notification-detail-sheet__header { align-items: center; border-bottom: 1px solid rgba(0, 0, 0, 0.12); justify-content: space-between; }
.theme--dark .notification-detail-sheet__header { border-bottom-color: rgba(255, 255, 255, 0.16); }
.notification-detail-sheet__body { min-height: 0; overflow-y: auto; padding: 20px; }
.notification-detail-sheet__actions { border-top: 1px solid rgba(0, 0, 0, 0.12); flex: 0 0 auto; padding: 12px 20px calc(12px + env(safe-area-inset-bottom)); }
.theme--dark .notification-detail-sheet__actions { border-top-color: rgba(255, 255, 255, 0.16); }
.notification-detail-sheet__message { font-size: 1rem; line-height: 1.55; white-space: pre-wrap; overflow-wrap: anywhere; }
</style>
